import * as Sentry from "@sentry/react";

// Central helper for reporting errors from critical integration flows
// (mini-app handshake, bet placement, deposit/withdrawal) to Sentry.
//
// Many catch blocks in the app only console.log their errors, which makes
// real failures invisible. This helper gives those flows a one-liner that:
//   - skips errors already reported by the http client (`sentryReported`),
//   - skips transient client-network drops (status 0 / no status) — those are
//     environment noise, already retried by the integrated http client,
//   - tags the event with the owning feature so it's filterable in Sentry.
//
// Use it ONLY for flows that matter (entering the app, placing a bet, moving
// money). Do NOT blanket-wrap every catch — that reintroduces the noise we
// just cleaned up.
export const reportError = (
  error: any,
  feature: string,
  extra?: Record<string, unknown>
) => {
  console.error(`[${feature}]`, error);

  if (error?.sentryReported) return;

  const status = error?.status ?? error?.response?.status;
  // status 0 / undefined === request never completed (WebView network drop).
  const isTransientNetwork =
    error?.name === "MissingIntegrationAuthError" ||
    status === 0 ||
    status === undefined;
  if (isTransientNetwork) return;

  Sentry.captureException(error, {
    tags: { feature, ...(status !== undefined ? { http_status: String(status) } : {}) },
    extra: { status, ...(extra || {}) },
  });
};
